export enum Difficulty {
  EASY = 'Leicht',
  MEDIUM = 'Mittel',
  HARD = 'Schwer',
}

export enum QuestionType {
  SINGLE_CHOICE = 'Single Choice',
  MULTIPLE_CHOICE = 'Multiple Choice',
  SITUATION = 'Situation',
}

export interface Question {
  id: string;
  chapterId: number;
  difficulty: Difficulty;
  type: QuestionType;
  questionText: string;
  options: { id: string; text: string }[];
  correctOptionIds: string[];
  explanation: string; // Static fallback explanation
  tags: string[];
  imagePlaceholder?: string; // URL for image if needed
  videoUrl?: string; // URL for video questions
  visualPrompt?: string; // Specific prompt for AI image generation
  points: number; // Official error points (2-5)
  aiExplanation?: string; // Pre-generated AI explanation (richer than static)
}

export interface Chapter {
  id: number;
  title: string;
  description: string;
  videoPlaceholder: string; // URL
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string; // Lucide icon name
  unlockedAt: string;
}

export interface SRSData {
  level: number; // 0-5
  nextReview: number; // timestamp
  /** Timestamp of the last answer that produced this entry — lets the
   * cross-device merge pick the newer entry (Paket B). Absent on legacy
   * entries written before 2026-08. */
  updatedAt?: number;
}

export interface UserProfile {
  name: string;
  displayName?: string; // Custom leaderboard name (privacy)
  email: string;
  xp: number;
  level: number;
  streak: number;
  /** Local date (yyyy-mm-dd) of the last day that counted towards the streak (F-03). */
  lastStreakDate?: string;
  lastLoginDate: string;
  completedQuestions: string[]; // IDs
  correctlyAnswered: string[]; // IDs
  bookmarkedQuestions: string[]; // IDs of favorited questions
  chapterProgress: Record<number, number>; // chapterId -> percentage
  isPremium: boolean;
  premiumSince?: string; // ISO timestamp when premium was activated (maps to premium_since)
  premiumProduct?: string; // Identifier of the purchased premium plan (maps to premium_product)
  simulationAttempts: number;
  isDarkMode: boolean; // New preference
  language: string; // User language preference (e.g., 'de', 'en', 'tr')
  shareDataWithAI: boolean; // GDPR: Privacy preference
  consentGiven: boolean | null; // GDPR: Explicit consent status
  hasSeenOnboarding: boolean; // New onboarding flag
  badges: Badge[]; // Gamification
  failedQuestions: string[]; // IDs of questions answered incorrectly
  srsData: Record<string, SRSData>; // Spaced Repetition System data
  dailyGoal: number; // Daily question goal (default 20)
  dailyProgress: { date: string; count: number }; // Today's progress
  examDate: string | null; // Exam date (ISO string) for countdown
  profilePhotoUrl?: string; // Avatar photo URL
  gender?: 'männlich' | 'weiblich' | 'divers'; // Gender
  age?: number; // Age
  location?: string; // City/Location
  leaderboardVisible?: boolean; // Opt-in for leaderboard
  leaderboardSharedFields?: string[]; // Which fields to share: name, gender, photo, location, age
  experienceLevel?: 'neuling' | 'fortgeschritten' | 'erfahren'; // Experience with driving theory
  simulationHistory?: SimulationResult[]; // Past exam simulation results
  essencesRead?: number[]; // Chapter IDs where user has read the essence (+5 XP each, once)
  bonusChapters?: number; // Free referral: extra chapters unlocked (0-5)
  referralCode?: string; // Personal referral code
  referralCount?: number; // Number of qualified referrals (maps to referral_count)
  referredBy?: string; // Code used at registration
  referredByUserId?: string; // UUID of user who referred
  referralQualified?: boolean; // Whether this user qualified as a referral
  referralRewardPending?: boolean; // Premium referral: reward pending until purchase
  referralRewardApplied?: boolean; // Premium referral: reward was applied
  lastSeenWhatsNew?: string; // Last What's New version the user dismissed
  seenSpotlights?: string[]; // Feature spotlight IDs already seen
  dismissedAnnouncements?: string[]; // In-app announcement IDs dismissed
}

export interface ChapterEssence {
  chapterId: number;
  language: string;
  summary: string;
  goldenRules: { emoji: string; rule: string }[];
  examTip: string;
  commonMistakes: string[];
  mnemonic: string;
  coreQuestion?: string;
}

export interface SimulationResult {
  date: string; // ISO timestamp
  passed: boolean;
  correctCount: number;
  errorPoints: number;
  timeUsed?: number; // seconds
  chapterErrors?: Record<number, number>;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  timestamp: number;
}

export interface Conversation {
  id: string;
  userId: string;
  title: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}
